import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';

export type AnalyticsConsentChoice = 'accepted' | 'rejected' | null;

const CONSENT_POLICY_VERSION = 1;
const CONSENT_STORAGE_KEY = 'imagise.analytics-consent';

export const ANALYTICS_CONSENT_SETTINGS_ID = 'analytics-consent-settings';

@Injectable({ providedIn: 'root' })
export class AnalyticsConsentService {
  readonly #document = inject(DOCUMENT);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #choice = signal<AnalyticsConsentChoice>(null);
  readonly #initialized = signal(false);
  readonly #settingsOpen = signal(false);

  readonly choice = this.#choice.asReadonly();
  readonly initialized = this.#initialized.asReadonly();
  readonly settingsOpen = this.#settingsOpen.asReadonly();

  initialize(): void {
    if (this.#initialized() || !isPlatformBrowser(this.#platformId)) {
      return;
    }

    this.#choice.set(this.#readChoice());
    this.#initialized.set(true);
  }

  openSettings(): void {
    this.initialize();
    this.#settingsOpen.set(true);
  }

  closeSettings(): void {
    this.#settingsOpen.set(false);
  }

  /** Resets in-memory state between unit tests. */
  resetState(): void {
    this.#choice.set(null);
    this.#initialized.set(false);
    this.#settingsOpen.set(false);

    const storage = this.#storage();
    if (storage) {
      this.#removeStoredChoice(storage);
    }
  }

  accept(): void {
    this.#setChoice('accepted');
  }

  reject(): void {
    this.#setChoice('rejected');
  }

  #setChoice(choice: Exclude<AnalyticsConsentChoice, null>): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    this.#choice.set(choice);
    this.#initialized.set(true);
    this.#settingsOpen.set(false);
    this.#writeChoice(choice);
  }

  #readChoice(): AnalyticsConsentChoice {
    const storage = this.#storage();
    if (!storage) {
      return null;
    }

    try {
      const raw = storage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const stored = JSON.parse(raw) as unknown;
      if (typeof stored === 'object' && stored !== null && Reflect.get(stored, 'version') === CONSENT_POLICY_VERSION) {
        const choice = Reflect.get(stored, 'choice');
        if (choice === 'accepted' || choice === 'rejected') {
          return choice;
        }
      }
    } catch {
      // Invalid or unavailable storage is treated as no choice.
    }

    this.#removeStoredChoice(storage);
    return null;
  }

  #writeChoice(choice: Exclude<AnalyticsConsentChoice, null>): void {
    const storage = this.#storage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ version: CONSENT_POLICY_VERSION, choice }));
    } catch {
      // Do not leave an older accepted choice behind after a failed withdrawal.
      this.#removeStoredChoice(storage);
    }
  }

  #removeStoredChoice(storage: Storage): void {
    try {
      storage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      // The current in-memory choice remains authoritative for this tab.
    }
  }

  #storage(): Storage | null {
    if (!isPlatformBrowser(this.#platformId)) {
      return null;
    }

    try {
      return this.#document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}
